import { randomWeighted } from "@softgames/random-utils";
import { dailyJackpotRewards, LUCKY_WHEEL_MIN_LEVEL, luckyWheelRewards, RESET_SPIN_HOURS } from "./LuckyWheelConstants";
import type { LuckyWheelDependencies, LuckyWheelObservable } from "./types";

export function isLuckyWheelAvailable(observable: LuckyWheelObservable): boolean {
  const { levelIndex } = observable.getValue();
  return levelIndex + 1 >= LUCKY_WHEEL_MIN_LEVEL;
}

export function getNextSpinDate(lastSpinTime: number): Date {
  const lastSpinDate = new Date(lastSpinTime);
  
  const lastSpinHour = lastSpinDate.getHours();
  // Assuming that all the spin hours are sorted in increasing order
  const nextSlotHour = RESET_SPIN_HOURS.find((hour) => hour > lastSpinHour);

  if (nextSlotHour === undefined) {
    // We couldn't find an hour after the current date, so the next spin
    // will be available on the earliest hour of the following day
    lastSpinDate.setDate(lastSpinDate.getDate() + 1);
    lastSpinDate.setHours(RESET_SPIN_HOURS[0], 0, 0, 0);
  } else {
    lastSpinDate.setHours(nextSlotHour, 0, 0, 0);
  }

  return lastSpinDate;
}

export function getNextSpinTime(lastSpinTime: number): number {
  return getNextSpinDate(lastSpinTime).getTime();
}

export function getPreviousSpinDate(lastSpinTime: number): Date {
  const lastSpinDate = new Date(lastSpinTime);

  const lastSpinHour = lastSpinDate.getHours();
  // Assuming that all the spin hours are sorted in increasing order
  const previousSlotHour = RESET_SPIN_HOURS.slice()
    .reverse()
    .find((hour) => hour <= lastSpinHour);

  if (previousSlotHour === undefined) {
    lastSpinDate.setDate(lastSpinDate.getDate() - 1);
    lastSpinDate.setHours(RESET_SPIN_HOURS[RESET_SPIN_HOURS.length - 1], 0, 0, 0);
  } else {
    lastSpinDate.setHours(previousSlotHour, 0, 0, 0);
  }

  return lastSpinDate;
}

export function getSpinDateRange(lastSpinTime: number): { startDate: Date; endDate: Date } {
  return {
    startDate: getPreviousSpinDate(lastSpinTime),
    endDate: getNextSpinDate(lastSpinTime),
  };
}

export function hasRewardedFreeSpinsToUse(observable: LuckyWheelObservable): boolean {
  const { luckyWheel } = observable.getValue();
  return (luckyWheel.rewardedFreeSpins ?? 0) > 0;
}

export function hasBonusFreeSpin(observable: LuckyWheelObservable): boolean {
  const { luckyWheel } = observable.getValue();
  const range = getSpinDateRange(luckyWheel.bonusFreeSpinTime);
  const currentTime = Date.now();

  return currentTime >= range.startDate.getTime() && currentTime < range.endDate.getTime();
}

export function useBonusFreeSpin(observable: LuckyWheelObservable): void {
  const { luckyWheel } = observable.getValue();

    observable.updateValue({
      luckyWheel: {
        ...luckyWheel,
        bonusFreeSpinTime: 0,
      },
    });
}

export function getTotalFreeSpins(observable: LuckyWheelObservable): number {
  const { luckyWheel } = observable.getValue();

  let count = luckyWheel.rewardedFreeSpins;
  if (hasBonusFreeSpin(observable) || (count === 0 && Date.now() >= getNextSpinTime(luckyWheel.lastFreeSpinTime))) {
    count += 1;
  }

  return count;
}

export function isFreeSpinAvailable(observable: LuckyWheelObservable): boolean {
  const { luckyWheel } = observable.getValue();

  const nextFreeSpinDate = getNextSpinDate(luckyWheel.lastFreeSpinTime);

  return (
    hasRewardedFreeSpinsToUse(observable) || hasBonusFreeSpin(observable) || Date.now() >= nextFreeSpinDate.getTime()
  );
}

export function isAdSpinAvailable(observable: LuckyWheelObservable): boolean {
  const { luckyWheel } = observable.getValue();

  const currentTime = Date.now();
  const spinRange = getSpinDateRange(luckyWheel.lastAdSpinTime);

  return (
    currentTime >= spinRange.endDate.getTime() ||
    (currentTime > spinRange.startDate.getTime() && luckyWheel.adSpinCount > 0)
  );
}

export function updateFreeSpinData(dependencies: LuckyWheelDependencies, wonRewardIndex: number): void {
  const { observable } = dependencies;
  const { luckyWheel } = observable.getValue();

  observable.updateValue({
    luckyWheel: {
      ...luckyWheel,
      lastFreeSpinTime: Date.now(),
      adSpinCount: getMaxAdSpinsCount(dependencies),
      lastWonRewardIndex: wonRewardIndex,
    },
  });
}

export function updateAdSpinData(dependencies: LuckyWheelDependencies, wonRewardIndex: number): void {
  const { observable } = dependencies;
  const { luckyWheel } = observable.getValue();

  observable.updateValue({
    luckyWheel: {
      ...luckyWheel,
      lastAdSpinTime: Date.now(),
      adSpinCount: Math.max(0, luckyWheel.adSpinCount - 1),
      lastWonRewardIndex: wonRewardIndex,
    },
  });
}

export function computeRewardFromSpin(observable: LuckyWheelObservable, ignoreLastReward: boolean): number {
  const { luckyWheel } = observable.getValue();
  const { lastWonRewardIndex } = luckyWheel;

  let rewardIndex = -1;
  do {
    rewardIndex = randomWeighted(luckyWheelRewards.map((reward) => reward.odds));
  } while (!ignoreLastReward && lastWonRewardIndex && rewardIndex === lastWonRewardIndex);

  return rewardIndex;
}

export function getDailyJackpotRewardIndex(): number {
  const currentDate = new Date();
  // getDay returns 0 for Sunday, 1 for Monday and so on
  return currentDate.getDay() % dailyJackpotRewards.length;
}

export function getMaxAdSpinsCount(dependencies: LuckyWheelDependencies): number {
  return dependencies.getAdSpins();
}